import React, { useState, useEffect } from 'react';
import { quizAPI, courseAPI, lessonAPI, questionAPI, cacheUtils } from '../../services/api';
import { DataTable } from '../ui/data-table';

const Quizzes = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingQuizId, setEditingQuizId] = useState(null);
  const [deletingQuizId, setDeletingQuizId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_id: 0,
    lesson_id:  0,
  });

  // Questions modal state
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
  const [selectedQuizForQuestions, setSelectedQuizForQuestions] = useState(null);
  const [allQuestions, setAllQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionSearchTerm, setQuestionSearchTerm] = useState('');
  const [assigningQuestions, setAssigningQuestions] = useState(false);

  // View questions modal state
  const [isViewQuestionsModalOpen, setIsViewQuestionsModalOpen] = useState(false);
  const [selectedQuizForViewing, setSelectedQuizForViewing] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizQuestionsLoading, setQuizQuestionsLoading] = useState(false);
  const [removingQuestionId, setRemovingQuestionId] = useState(null);

  useEffect(() => {
    fetchQuizzes();
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      fetchCourses();
    }
  }, [isModalOpen]);

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      const response = await quizAPI.getAllQuizzes();
      setQuizzes(response.data || []);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      setCoursesLoading(true);
      const response = await courseAPI.getAllCourses();
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setCoursesLoading(false);
    }
  };

  const fetchLessonsByCourse = async (courseId) => {
    if (!courseId) {
      setLessons([]);
      return;
    }

    try {
      setLessonsLoading(true);
      const response = await lessonAPI.getAllLessonsByCourseId(courseId);
      setLessons(response.data || []);
    } catch (error) {
      console.error('Error fetching lessons:', error);
      setLessons([]);
    } finally {
      setLessonsLoading(false);
    }
  };

  const fetchQuestionsByLessonID = async (lessonId) => {
    try {
      setQuestionsLoading(true);
      if (!lessonId) {
        setAllQuestions([]);
        return;
      }
      const response = await questionAPI.getQuestionsByLessonId(lessonId);
      setAllQuestions(response.data || []);
    } catch (error) {
      console.error('Error fetching questions by lesson ID:', error);
      setAllQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };
  
  const fetchQuestionsByQuizId = async (quizId) => {
    try {
      setQuizQuestionsLoading(true);
      const response = await questionAPI.getQuestionsByQuizId(quizId);
      setQuizQuestions(response.data || []);
    } catch (error) {
      console.error('Error fetching quiz questions:', error);
      setQuizQuestions([]);
    } finally {
      setQuizQuestionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value);
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue,
      // Reset lesson_id when course changes
      ...(name === 'course_id' && { lesson_id: 0 })
    }));

    // Fetch lessons when course is selected
    if (name === 'course_id' && value) {
      console.log('Selected course ID:', value);
      fetchLessonsByCourse(value);
    } else if (name === 'course_id' && !value) {
      setLessons([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const submitData = {
        ...formData,
        lesson_id: parseInt(formData.lesson_id),
      };

      if (isEditing) {
        const updateData = { ...submitData, id: editingQuizId };
        console.log('Updating quiz with data:', updateData);
        delete updateData.course_id; // course_id is not needed for update
        await quizAPI.updateQuiz(updateData);
      } else {
        // delete course_id from submitData as it's not needed for creation
        delete submitData.course_id;
        await quizAPI.createQuiz(submitData);
      }

      setIsModalOpen(false);
      resetForm();
      fetchQuizzes();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} quiz:`, error);
      alert(`Failed to ${isEditing ? 'update' : 'create'} quiz. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuiz = (quiz) => {
    setIsEditing(true);
    setEditingQuizId(quiz.id);
    setFormData({
      title: quiz.title || '',
      description: quiz.description || '',
      course_id: quiz.course_id || 0,
      lesson_id: quiz.lesson_id || 0,
    });
    
    // Fetch lessons for the selected course when editing
    if (quiz.course_id) {
      fetchLessonsByCourse(quiz.course_id);
    }
    
    setIsModalOpen(true);
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingQuizId(quizId);
      console.log('Deleting quiz with ID:', quizId);
      await quizAPI.deleteQuiz(quizId);
      fetchQuizzes();
    } catch (error) {
      console.error('Error deleting quiz:', error);
      alert('Failed to delete quiz. Please try again.');
    } finally {
      setDeletingQuizId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      course_id: 0,
      lesson_id: 0,
    });
    setLessons([]);
    setIsEditing(false);
    setEditingQuizId(null);
  };

  const handleSelectQuestions = (quiz) => {
    setSelectedQuizForQuestions(quiz);
    setSelectedQuestions([]);
    setQuestionSearchTerm('');
    setIsQuestionsModalOpen(true);
    fetchQuestionsByLessonID(quiz.lesson_id);
  };

  const handleViewQuestions = (quiz) => {
    setSelectedQuizForViewing(quiz);
    setIsViewQuestionsModalOpen(true);
    fetchQuestionsByQuizId(quiz.id);
  };

  const handleQuestionToggle = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleAssignQuestions = async () => {
    if (!selectedQuizForQuestions || selectedQuestions.length === 0) {
      alert('Please select at least one question.');
      return;
    }

    try {
      setAssigningQuestions(true);
      
      // Send each question with quiz_id in a loop as requested
      for (const questionId of selectedQuestions) {
        const data = {
          quiz_id: selectedQuizForQuestions.id,
          id: questionId
        };
        await questionAPI.updateQuestionsQuizId(data);
      }

      alert(`Successfully assigned ${selectedQuestions.length} questions to the quiz.`);
      setIsQuestionsModalOpen(false);
      setSelectedQuestions([]);
      setSelectedQuizForQuestions(null);
    } catch (error) {
      console.error('Error assigning questions:', error);
      alert('Failed to assign questions. Please try again.');
    } finally {
      setAssigningQuestions(false);
    }
  };

  const handleRemoveQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to remove this question from the quiz?')) {
      return;
    }

    try {
      setRemovingQuestionId(questionId);
      
      // Set quiz_id to 0 to remove the question from the quiz
      const data = {
        quiz_id: 0,
        id: questionId
      };
      
      console.log('Removing question with data:', data);
      await questionAPI.updateQuestionsQuizId(data);
      
      // Refresh the questions list for the current quiz
      if (selectedQuizForViewing) {
        fetchQuestionsByQuizId(selectedQuizForViewing.id);
      }
      
    } catch (error) {
      console.error('Error removing question:', error);
      alert('Failed to remove question. Please try again.');
    } finally {
      setRemovingQuestionId(null);
    }
  };

  const filteredQuestions = allQuestions.filter(question => {
    // First filter by search term
    const matchesSearch = question.question_text?.toLowerCase().includes(questionSearchTerm.toLowerCase()) ||
      question.id?.toString().includes(questionSearchTerm);
    
    // Then exclude questions that are already assigned to the current quiz
    const isNotAlreadyAssigned = !quizQuestions.some(quizQuestion => quizQuestion.id === question.id);
    
    return matchesSearch && isNotAlreadyAssigned;
  });

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      className: 'w-16',
    },
    {
      accessorKey: 'title',
      header: 'Title',
      className: 'min-w-48',
    },
    {
      accessorKey: 'description',
      header: 'Description',
      className: 'min-w-64',
    },
    {
      accessorKey: 'lesson_id',
      header: 'Lesson ID',
      className: 'w-24',
      cell: (row) => row.lesson_id || 'No lesson'
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      className: 'w-56',
      cell: (row) => (
        <div className="flex space-x-1">
          <button
            onClick={() => handleEditQuiz(row)}
            className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
            title="Edit quiz"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleSelectQuestions(row)}
            className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
            title="Select questions"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </button>
          <button
            onClick={() => handleViewQuestions(row)}
            className="p-2 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-full transition-colors"
            title="See questions"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteQuiz(row.id)}
            disabled={deletingQuizId === row.id}
            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete quiz"
          >
            {deletingQuizId === row.id ? (
              <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-7xl mx-auto p-8">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Quiz Management</h1>
            <p className="text-lg text-gray-600">Create and manage quizzes and assessments</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              + Add Quiz
            </button>
            <button
              onClick={() => {
                cacheUtils.clearQuizCache();
                fetchQuizzes();
              }}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              🔄 Refresh
            </button>
            <button
              onClick={() => cacheUtils.clearAllCache()}
              className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
            >
              🗑️ Clear Cache
            </button>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={quizzes}
        searchKey="title"
        searchPlaceholder="Search quizzes..."
        loading={loading}
        emptyMessage="No quizzes found. Create your first quiz!"
        pageSize={10}
      />

      {/* Quiz Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Edit Quiz' : 'Create New Quiz'}
              </h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Quiz Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quiz Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter quiz title..."
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter quiz description..."
                />
              </div>

              {/* Course Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course
                </label>
                <select
                  name="course_id"
                  value={formData.course_id}
                  onChange={handleInputChange}
                  disabled={coursesLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                >
                  <option value="">
                    {coursesLoading ? 'Loading courses...' : 'Select a course'}
                  </option>
                  {!coursesLoading && courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title || `Course ${course.id}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lesson Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lesson
                </label>
                <select
                  name="lesson_id"
                  value={formData.lesson_id}
                  onChange={handleInputChange}
                  disabled={!formData.course_id || lessonsLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                >
                  <option value="">
                    {!formData.course_id 
                      ? 'Select a course first' 
                      : lessonsLoading 
                        ? 'Loading lessons...' 
                        : 'Select a lesson'
                    }
                  </option>
                  {!lessonsLoading && formData.course_id && lessons.map((lesson) => (
                    <option key={lesson.id} value={lesson.id}>
                      {lesson.title || `Lesson ${lesson.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {loading 
                    ? (isEditing ? 'Updating...' : 'Creating...') 
                    : (isEditing ? 'Update Quiz' : 'Create Quiz')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Questions Selection Modal */}
      {isQuestionsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Select Questions for "{selectedQuizForQuestions?.title}"
              </h2>
              <button
                onClick={() => {
                  setIsQuestionsModalOpen(false);
                  setSelectedQuestions([]);
                  setSelectedQuizForQuestions(null);
                  setQuestionSearchTerm('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Search Input */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search questions..."
                value={questionSearchTerm}
                onChange={(e) => setQuestionSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Selected Count */}
            <div className="mb-4 p-3 bg-blue-50 rounded-md">
              <p className="text-sm text-blue-700">
                {selectedQuestions.length} question(s) selected
              </p>
            </div>

            {/* Questions List */}
            <div className="mb-6 max-h-96 overflow-y-auto border border-gray-200 rounded-md">
              {questionsLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin mx-auto w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                  <p className="mt-2 text-gray-600">Loading questions...</p>
                </div>
              ) : filteredQuestions.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {questionSearchTerm ? 'No questions found matching your search.' : 'No questions available.'}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredQuestions.map((question) => (
                    <div key={question.id} className="p-4 hover:bg-gray-50">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedQuestions.includes(question.id)}
                          onChange={() => handleQuestionToggle(question.id)}
                          className="mt-1 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              ID: {question.id}
                            </span>
                            {question.question_type && (
                              <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                {question.question_type}
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            {question.text || 'No question text'}
                          </p>
                          {question.description && (
                            <p className="text-xs text-gray-600">
                              {question.description}
                            </p>
                          )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setIsQuestionsModalOpen(false);
                  setSelectedQuestions([]);
                  setSelectedQuizForQuestions(null);
                  setQuestionSearchTerm('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignQuestions}
                disabled={assigningQuestions || selectedQuestions.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {assigningQuestions 
                  ? 'Assigning...' 
                  : `Assign ${selectedQuestions.length} Question${selectedQuestions.length !== 1 ? 's' : ''}`
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Questions Modal */}
      {isViewQuestionsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Questions in "{selectedQuizForViewing?.title}"
              </h2>
              <button
                onClick={() => {
                  setIsViewQuestionsModalOpen(false);
                  setSelectedQuizForViewing(null);
                  setQuizQuestions([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Questions Count */}
            <div className="mb-4 p-3 bg-purple-50 rounded-md">
              <p className="text-sm text-purple-700">
                {quizQuestions.length} question(s) in this quiz
              </p>
            </div>

            {/* Questions List */}
            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
              {quizQuestionsLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin mx-auto w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
                  <p className="mt-2 text-gray-600">Loading questions...</p>
                </div>
              ) : quizQuestions.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <svg className="mx-auto w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-lg font-medium text-gray-900 mb-2">No questions found</p>
                  <p className="text-gray-500">This quiz doesn't have any questions assigned yet.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {quizQuestions.map((question, index) => (
                    <div key={question.id} className="p-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              ID: {question.id}
                            </span>
                            {question.question_type && (
                              <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded">
                                {question.question_type}
                              </span>
                            )}
                            {question.marks && (
                              <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                                {question.marks} marks
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-900 mb-2">
                            {question.text || question.question_text || 'No question text'}
                          </p>
                          {question.description && (
                            <p className="text-xs text-gray-600 mb-2">
                              {question.description}
                            </p>
                          )}
                          {question.options && question.options.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-gray-700 mb-1">Options:</p>
                              <div className="space-y-1">
                                {question.options.map((option, optIndex) => (
                                  <div key={optIndex} className="flex items-center space-x-2">
                                    <span className="text-xs text-gray-500 w-4">
                                      {String.fromCharCode(65 + optIndex)}.
                                    </span>
                                    <span className="text-xs text-gray-700">
                                      {option.text || option}
                                    </span>
                                    {option.is_correct && (
                                      <span className="text-xs text-green-600 font-medium">✓</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <button
                            onClick={() => handleRemoveQuestion(question.id)}
                            disabled={removingQuestionId === question.id}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remove question from quiz"
                          >
                            {removingQuestionId === question.id ? (
                              <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Close Button */}
            <div className="flex justify-end pt-4 border-t mt-6">
              <button
                onClick={() => {
                  setIsViewQuestionsModalOpen(false);
                  setSelectedQuizForViewing(null);
                  setQuizQuestions([]);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quizzes;